import { computed, ref, watch, type ComputedRef } from 'vue';
import { useTableEditorStore } from '../model/table-editor.store';
import { useTableProperties } from '../model/useTableProperties';
import {
  extractBorderProperties,
  resolveBorderPattern,
  type BorderPattern,
  type BorderStyleType,
} from '../lib/cell-border-utils';
import { sanitizeColor, clampNumber } from '../lib/color-sanitizer';
import { TABLE_BORDER_DEFAULTS } from '../lib/table-constants';
import TableBorderPatternAllIcon from '@/shared/assets/icons/TableBorderPatternAllIcon.svg';
import TableBorderPatternTopIcon from '@/shared/assets/icons/TableBorderPatternTopIcon.svg';
import TableBorderPatternRightIcon from '@/shared/assets/icons/TableBorderPatternRightIcon.svg';
import TableBorderPatternBottomIcon from '@/shared/assets/icons/TableBorderPatternBottomIcon.svg';
import TableBorderPatternLeftIcon from '@/shared/assets/icons/TableBorderPatternLeftIcon.svg';
import TableBorderPatternTopBottomIcon from '@/shared/assets/icons/TableBorderPatternTopBottomIcon.svg';
import TableBorderPatternLeftRightIcon from '@/shared/assets/icons/TableBorderPatternLeftRightIcon.svg';
import TableBorderPatternNoneIcon from '@/shared/assets/icons/TableBorderPatternNoneIcon.svg';

const BORDER_PATTERN_ICON_MAP: Record<BorderPattern, string | object> = {
  all: TableBorderPatternAllIcon,
  top: TableBorderPatternTopIcon,
  right: TableBorderPatternRightIcon,
  bottom: TableBorderPatternBottomIcon,
  left: TableBorderPatternLeftIcon,
  'top-bottom': TableBorderPatternTopBottomIcon,
  'left-right': TableBorderPatternLeftRightIcon,
  none: TableBorderPatternNoneIcon,
};

/**
 * Border pattern/width/style/color state for the table cell-selection menu.
 * When `isTableVariant` is true, writes go through the table-level properties;
 * otherwise they apply to the selected cells' borders.
 */
export function useTableMenuBorders(isTableVariant: ComputedRef<boolean>) {
  const tableEditorStore = useTableEditorStore();
  const activeTableIdRef = computed(() => tableEditorStore.activeTableId ?? '');
  const tablePropsComposable = useTableProperties(activeTableIdRef);

  const borderPatternOpen = ref(false);
  const borderWidthOpen = ref(false);
  const borderStyleOpen = ref(false);
  const selectedPattern = ref<BorderPattern | null>(null);

  const firstSelectedCell = computed(() => {
    const cells = tableEditorStore.selectedCellsData;
    return cells.length > 0 ? cells[0] : null;
  });

  watch(firstSelectedCell, () => {
    selectedPattern.value = null;
  });

  const borderProps = computed(() => extractBorderProperties(firstSelectedCell.value?.properties));

  const currentPattern = computed(
    () => selectedPattern.value ?? resolveBorderPattern(firstSelectedCell.value?.properties)
  );

  const currentPatternIcon = computed(() => BORDER_PATTERN_ICON_MAP[currentPattern.value]);

  const borderColor = computed({
    get: () => (isTableVariant.value ? tablePropsComposable.borderColor.value : borderProps.value.color),
    set: (value: string) => {
      if (isTableVariant.value) {
        tablePropsComposable.updateProperties({
          borderColor: sanitizeColor(value, TABLE_BORDER_DEFAULTS.COLOR),
        });
        return;
      }
      tableEditorStore.updateCellBordersPattern(currentPattern.value, {
        color: sanitizeColor(value, TABLE_BORDER_DEFAULTS.COLOR),
        width: borderProps.value.width,
        style: borderProps.value.style,
      });
    },
  });

  const borderWidth = computed({
    get: () => (isTableVariant.value ? tablePropsComposable.borderWidth.value : borderProps.value.width),
    set: (value: number) => {
      if (isTableVariant.value) {
        tablePropsComposable.setBorderWidth(value);
        return;
      }
      tableEditorStore.updateCellBordersPattern(currentPattern.value, {
        color: borderProps.value.color,
        width: clampNumber(value, 1, 5),
        style: borderProps.value.style,
      });
    },
  });

  const borderStyle = computed({
    get: () => (isTableVariant.value ? tablePropsComposable.borderStyle.value : borderProps.value.style),
    set: (value: BorderStyleType) => {
      if (value === 'none') return;
      if (isTableVariant.value) {
        tablePropsComposable.updateProperties({ borderStyle: value });
        return;
      }
      tableEditorStore.updateCellBordersPattern(currentPattern.value, {
        color: borderProps.value.color,
        width: borderProps.value.width,
        style: value,
      });
    },
  });

  const handlePatternChange = (pattern: BorderPattern) => {
    selectedPattern.value = pattern;
    tableEditorStore.updateCellBordersPattern(pattern, {
      color: borderProps.value.color,
      width: borderProps.value.width,
      style: borderProps.value.style,
    });
    borderPatternOpen.value = false;
  };

  return {
    borderPatternOpen,
    borderWidthOpen,
    borderStyleOpen,
    currentPattern,
    currentPatternIcon,
    borderColor,
    borderWidth,
    borderStyle,
    handlePatternChange,
  };
}
